'use client';

/**
 * HandoffRecapCard — v3 turn 5 "graceful end" treatment for a finished
 * live-agent handoff (HandoffPhase.ENDED — HANDOFF_ENDED only; HANDOFF_TIMEOUT
 * now produces its own HandoffPhase.TIMED_OUT and is surfaced by
 * presence-ribbon.tsx's timeout variant instead — see that phase's JSDoc in
 * types/chat.ts for why they were split). Renders ONCE, in the transcript
 * flow, at the point the handoff ended — never a persistent/pinned surface
 * (contrast with PresenceRibbon, which IS pinned for the duration of a LIVE
 * handoff).
 *
 * Integration point (see message-block.tsx / page.tsx): the backend already
 * sends an end-of-handoff notice as a plain SYSTEM-role Message (e.g. "Your
 * chat with Priya has ended."). page.tsx detects the live
 * handoff→ENDED transition and hands the matching Message's id to
 * message-block.tsx, which renders THIS component in place of that row's
 * plain grey system-notice bubble — same "supersedes systemContent" pattern
 * ContactSummaryCard already uses for the CONTACT_SUMMARY row.
 *
 * Deliberately omits action chips ("Add to calendar" / "Rate this chat") —
 * neither has a handler or backend data behind it; a chip with no effect
 * would be a worse affordance than no chip at all.
 *
 * `endReason` (timeout-recovery flow, see handoff-timeout-panel.tsx): a
 * handoff whose end_reason is TIMEOUT_NO_AGENT must NOT render this card at
 * all — HandoffTimeoutPanel already owns that moment's messaging (the "no
 * agent available" recovery options), and stacking this generic "You're
 * back with Sara" copy on top of it would contradict a flow the user may
 * still be actively working through. Every other end_reason (agent_resolved,
 * user_ended, error, or absent) renders exactly as before.
 *
 * Motion: mirrors ContactSummaryCard's expand-in (same easing/duration),
 * honors prefers-reduced-motion, and is skipped for restored transcript rows
 * (a reloaded page must not replay entrance animations).
 *
 * Built on StandardCard like every other inline card. It was the last one
 * still hand-rolling its own card markup, which meant it also hand-rolled the
 * width lane — the divergence that let conversation-rating-card ship
 * full-bleed. Standardising it cost this card its Lora heading (StandardCard
 * titles are Work Sans) and moved the body copy from beside the orb to
 * full-width beneath the header row; both were accepted deliberately, because
 * a card that stays typographically special is a card the next one copies.
 */
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { HandoffEndReason } from '@/types/chat';
import { OrbAvatar } from './primitives/orb-avatar';
import { StandardCard } from './primitives/standard-card';

export interface HandoffRecapCardProps {
  /** Display name of the rep who left. Falls back to a generic label so this
   *  never fabricates a name that was never actually reported (mirrors
   *  handoff-banner-text.ts's DEFAULT_AGENT_LABEL fallback). */
  agentDisplayName?: string;
  /** The server's reason code for why the handoff ended (see this file's
   *  header for the TIMEOUT_NO_AGENT suppression rule). Undefined renders
   *  the normal recap (matches all pre-existing call sites). */
  endReason?: HandoffEndReason;
  /** Play the live expand-in (false for restored transcript rows). Defaults
   *  false — static render is the safe fallback for any call site that
   *  doesn't know. */
  animateIn?: boolean;
}

const DEFAULT_AGENT_LABEL = 'Our support team';

export function HandoffRecapCard({
  agentDisplayName,
  endReason,
  animateIn = false,
}: HandoffRecapCardProps) {
  const reducedMotion = useReducedMotion();
  const animate = animateIn && !reducedMotion;

  // Called after the hooks above (rules-of-hooks: hooks must run
  // unconditionally every render) — the timeout panel owns this moment
  // instead, see this file's header comment.
  if (endReason === HandoffEndReason.TIMEOUT_NO_AGENT) return null;

  return (
    <motion.div
      initial={animate ? { opacity: 0, height: 0, scale: 0.97 } : false}
      animate={{ opacity: 1, height: 'auto', scale: 1 }}
      transition={{
        duration: 0.32,
        delay: 0.18,
        ease: [0.16, 1, 0.3, 1],
      }}
      // No width lane here — StandardCard owns its own (see that primitive's
      // root comment). This wrapper's only job is stacking the chip above the
      // card and clipping the height animation.
      className="flex flex-col items-center gap-[12px] overflow-hidden"
    >
      {/* Centered system chip — "{agent} left the chat". */}
      <span
        className={cn(
          'rounded-[20px] bg-[#F1EBE1] px-[12px] py-1',
          "font-['Work_Sans'] text-[12.5px] font-semibold text-[#8a7c68]"
        )}
      >
        {agentDisplayName || DEFAULT_AGENT_LABEL} left the chat
      </span>

      {/* Recap card — hands the conversation back to Sara. */}
      <StandardCard
        tile={<OrbAvatar size={32} variant="chat" />}
        title="You're back with Sara"
      >
        I&apos;m here if you need anything else — happy to keep helping.
      </StandardCard>
    </motion.div>
  );
}
